/*
 * OC Recruitment — Contact Page
 * Design: "The Boardroom" Corporate Modernism
 * Simple, professional contact page with form + resume upload
 */

import { useState, useRef } from "react";
import { Mail, Phone, Linkedin, Send, Upload, X, FileText } from "lucide-react";
import { toast } from "sonner";
import ScrollReveal from "@/components/ScrollReveal";
import SEO from "@/components/SEO";
import { pageSEO, organizationSchema } from "@/lib/seoConfig";
import { trpc } from "@/lib/trpc";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
const ALLOWED_EXTENSIONS = ".pdf,.doc,.docx";

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove the data:...;base64, prefix
      const base64 = result.split(",")[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function Contact() {
  const seo = (
    <SEO
      title={pageSEO.contact.title}
      description={pageSEO.contact.description}
      keywords={pageSEO.contact.keywords}
      canonical={pageSEO.contact.canonical}
      structuredData={organizationSchema}
    />
  );
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    linkedin: "",
    type: "",
    message: "",
  });

  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const submitMutation = trpc.contact.submit.useMutation({
    onSuccess: (data) => {
      if (data.success) {
        toast.success("Message sent successfully", {
          description:
            "Thank you — Chris will be in touch the same business day.",
        });
        setFormData({
          name: "",
          email: "",
          phone: "",
          company: "",
          linkedin: "",
          type: "",
          message: "",
        });
        setResumeFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    },
    onError: (error) => {
      toast.error("Failed to send message", {
        description:
          "There was a problem sending your message. Please try again or email chris.kenny@oc-international.com directly.",
      });
      console.error("[Contact] Submission error:", error);
    },
  });

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error("Invalid file type", {
        description: "Please upload a PDF, DOC, or DOCX file.",
      });
      e.target.value = "";
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      toast.error("File too large", {
        description: "Maximum file size is 5MB. Please upload a smaller file.",
      });
      e.target.value = "";
      return;
    }

    setResumeFile(file);
  };

  const removeFile = () => {
    setResumeFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    let resumeData: { filename: string; mimeType: string; base64: string } | undefined;

    if (resumeFile) {
      try {
        const base64 = await fileToBase64(resumeFile);
        resumeData = {
          filename: resumeFile.name,
          mimeType: resumeFile.type,
          base64,
        };
      } catch {
        toast.error("Failed to process resume", {
          description: "There was a problem reading your file. Please try again.",
        });
        return;
      }
    }

    submitMutation.mutate({
      name: formData.name,
      email: formData.email,
      phone: formData.phone || undefined,
      company: formData.company || undefined,
      linkedin: formData.linkedin || undefined,
      type: formData.type,
      message: formData.message,
      resume: resumeData,
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div>
      {seo}
      {/* Hero */}
      <section className="bg-midnight pt-32 pb-20">
        <div className="container">
          <div className="max-w-3xl">
            <ScrollReveal>
              <p className="text-steel text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                Get in Touch
              </p>
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <h1 className="text-4xl sm:text-5xl text-white leading-[1.1] mb-6">
                Contact Chris
              </h1>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <p className="text-lg text-white/70 leading-relaxed">
                Whether you are a packaging company looking to hire or a sales
                professional exploring new opportunities, Chris is available
                for a confidential conversation.
              </p>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container">
          <div className="grid lg:grid-cols-5 gap-16">
            {/* Contact Info */}
            <div className="lg:col-span-2">
              <ScrollReveal>
                <h2 className="text-2xl text-midnight mb-8">
                  Contact Information
                </h2>
              </ScrollReveal>

              <div className="space-y-8">
                <ScrollReveal delay={0.1}>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded bg-midnight/5 flex items-center justify-center flex-shrink-0">
                      <Mail size={18} className="text-steel" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-steel mb-1">
                        Email
                      </p>
                      <a
                        href="mailto:chris.kenny@oc-international.com"
                        className="text-midnight hover:text-steel transition-colors font-medium"
                      >
                        chris.kenny@oc-international.com
                      </a>
                    </div>
                  </div>
                </ScrollReveal>

                <ScrollReveal delay={0.15}>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded bg-midnight/5 flex items-center justify-center flex-shrink-0">
                      <Phone size={18} className="text-steel" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-steel mb-1">
                        US / Chicago
                      </p>
                      <a
                        href="tel:+13128000868"
                        className="text-midnight hover:text-steel transition-colors font-medium"
                      >
                        +1 (312) 800-0868
                      </a>
                    </div>
                  </div>
                </ScrollReveal>

                <ScrollReveal delay={0.2}>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded bg-midnight/5 flex items-center justify-center flex-shrink-0">
                      <Linkedin size={18} className="text-steel" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-steel mb-1">
                        LinkedIn
                      </p>
                      <a
                        href="https://www.linkedin.com/in/packagingsalesrecruiter/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-midnight hover:text-steel transition-colors font-medium"
                      >
                        Connect on LinkedIn
                      </a>
                    </div>
                  </div>
                </ScrollReveal>


              </div>

              <ScrollReveal delay={0.3}>
                <div className="mt-12 p-6 bg-pale rounded border border-border">
                  <h3 className="text-base font-semibold text-midnight mb-3 font-['DM_Sans',sans-serif]">
                    Response Time
                  </h3>
                  <p className="text-sm text-slate-brand leading-relaxed">
                    Chris responds the same business day. For urgent searches, call the Chicago number directly.
                  </p>
                </div>
              </ScrollReveal>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <ScrollReveal>
                <h2 className="text-2xl text-midnight mb-8">
                  Send a Message
                </h2>
              </ScrollReveal>

              <ScrollReveal delay={0.1}>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-medium text-midnight mb-2"
                      >
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white border border-border rounded text-sm text-midnight placeholder:text-slate-brand/50 focus:outline-none focus:ring-2 focus:ring-steel/30 focus:border-steel transition-colors"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-midnight mb-2"
                      >
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white border border-border rounded text-sm text-midnight placeholder:text-slate-brand/50 focus:outline-none focus:ring-2 focus:ring-steel/30 focus:border-steel transition-colors"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="phone"
                        className="block text-sm font-medium text-midnight mb-2"
                      >
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white border border-border rounded text-sm text-midnight placeholder:text-slate-brand/50 focus:outline-none focus:ring-2 focus:ring-steel/30 focus:border-steel transition-colors"
                        placeholder="Your phone number"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="company"
                        className="block text-sm font-medium text-midnight mb-2"
                      >
                        Company
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white border border-border rounded text-sm text-midnight placeholder:text-slate-brand/50 focus:outline-none focus:ring-2 focus:ring-steel/30 focus:border-steel transition-colors"
                        placeholder="Your company name"
                      />
                    </div>
                  </div>

                  {/* LinkedIn Profile */}
                  <div>
                    <label
                      htmlFor="linkedin"
                      className="block text-sm font-medium text-midnight mb-2"
                    >
                      LinkedIn Profile
                      <span className="text-slate-brand/60 font-normal ml-1">(optional)</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                        <Linkedin size={16} className="text-slate-brand/40" />
                      </div>
                      <input
                        type="url"
                        id="linkedin"
                        name="linkedin"
                        value={formData.linkedin}
                        onChange={handleChange}
                        className="w-full pl-11 pr-4 py-3 bg-white border border-border rounded text-sm text-midnight placeholder:text-slate-brand/50 focus:outline-none focus:ring-2 focus:ring-steel/30 focus:border-steel transition-colors"
                        placeholder="https://www.linkedin.com/in/your-profile"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="type"
                      className="block text-sm font-medium text-midnight mb-2"
                    >
                      I am a... *
                    </label>
                    <select
                      id="type"
                      name="type"
                      required
                      value={formData.type}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border border-border rounded text-sm text-midnight focus:outline-none focus:ring-2 focus:ring-steel/30 focus:border-steel transition-colors"
                    >
                      <option value="" disabled>
                        Select an option
                      </option>
                      <option value="company">
                        Packaging company looking to hire
                      </option>
                      <option value="candidate">
                        Sales professional exploring opportunities
                      </option>
                      <option value="other">Other enquiry</option>
                    </select>
                  </div>

                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-midnight mb-2"
                    >
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={6}
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border border-border rounded text-sm text-midnight placeholder:text-slate-brand/50 focus:outline-none focus:ring-2 focus:ring-steel/30 focus:border-steel transition-colors resize-y"
                      placeholder="Tell Chris about your hiring needs or career goals..."
                    />
                  </div>

                  {/* Resume Upload */}
                  <div>
                    <label className="block text-sm font-medium text-midnight mb-2">
                      Attach Resume
                      <span className="text-slate-brand/60 font-normal ml-1">(optional — PDF, DOC, or DOCX, max 5MB)</span>
                    </label>

                    {!resumeFile ? (
                      <label
                        htmlFor="resume"
                        className="flex items-center justify-center gap-3 w-full px-6 py-8 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-steel/50 hover:bg-pale/50 transition-all duration-200 group"
                      >
                        <div className="flex flex-col items-center gap-2 text-center">
                          <div className="w-12 h-12 rounded-full bg-midnight/5 flex items-center justify-center group-hover:bg-steel/10 transition-colors">
                            <Upload size={20} className="text-slate-brand/60 group-hover:text-steel transition-colors" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-midnight">
                              Click to upload your resume
                            </p>
                            <p className="text-xs text-slate-brand/60 mt-1">
                              PDF, DOC, or DOCX up to 5MB
                            </p>
                          </div>
                        </div>
                        <input
                          type="file"
                          id="resume"
                          ref={fileInputRef}
                          accept={ALLOWED_EXTENSIONS}
                          onChange={handleFileChange}
                          className="hidden"
                        />
                      </label>
                    ) : (
                      <div className="flex items-center gap-4 px-4 py-3 bg-pale border border-border rounded-lg">
                        <div className="w-10 h-10 rounded bg-midnight/10 flex items-center justify-center flex-shrink-0">
                          <FileText size={18} className="text-steel" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-midnight truncate">
                            {resumeFile.name}
                          </p>
                          <p className="text-xs text-slate-brand/60">
                            {formatFileSize(resumeFile.size)}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={removeFile}
                          className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-midnight/10 transition-colors flex-shrink-0"
                          aria-label="Remove file"
                        >
                          <X size={16} className="text-slate-brand" />
                        </button>
                      </div>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={submitMutation.isPending}
                    className="inline-flex items-center gap-2 px-8 py-4 bg-midnight text-white text-sm font-semibold rounded hover:bg-midnight/90 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    {submitMutation.isPending ? (
                      <>Sending{resumeFile ? " (uploading resume...)" : "..."}</>
                    ) : (
                      <>
                        Send Message
                        <Send size={16} />
                      </>
                    )}
                  </button>
                </form>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
